/**
 * Copy + labels del camino día 1 (UI), separado del resolver puro.
 */
import type { Day1Milestone, Day1Phase, Day1StepId } from './resolveDay1Path';
import type { Day1NextCardCopy } from '../components/Day1NextCard';

export function day1StepLabels(serviceCatalog: boolean): Record<
  Day1StepId,
  { label: string; hint: string }
> {
  return {
    catalog: {
      label: serviceCatalog ? 'Cargar catálogo' : 'Cargar productos',
      hint: serviceCatalog ? 'Para poder agendar' : 'Para poder cobrar',
    },
    cash: {
      label: 'Abrir caja',
      hint: 'Si cobrás en efectivo',
    },
    sale: {
      label: 'Cobrar en el POS',
      hint: 'Una venta de prueba',
    },
    appointment: {
      label: 'Agendar un turno',
      hint: 'El primero de tu agenda',
    },
  };
}

export function day1NextCopy(input: {
  phase: Day1Phase;
  stepIndex: number;
  stepTotal: number;
  serviceCatalog: boolean;
  /** Hito del camino: primera venta o primer turno (capability appointments). */
  milestone: Day1Milestone;
  /** Si el camino incluye paso caja (para el hint “Después…”). */
  includesCashStep: boolean;
}): Day1NextCardCopy {
  const { phase, stepIndex, stepTotal, serviceCatalog, milestone, includesCashStep } = input;
  const progress = `Siguiente · ${stepIndex} de ${stepTotal}`;
  const pathTitle =
    milestone === 'appointment' ? 'Camino al primer turno' : 'Camino a la primera venta';

  if (phase === 'celebrate_first_sale') {
    return {
      eyebrow: 'Listo',
      title: 'Hiciste la primera venta',
      body: 'Ya podés seguir vendiendo.',
      cta: 'Seguir vendiendo',
      dismissLabel: 'Ocultar esta ayuda',
      pathTitle,
    };
  }

  if (phase === 'celebrate_first_appointment') {
    return {
      eyebrow: 'Listo',
      title: 'Agendaste el primer turno',
      body: 'Ya podés seguir agendando.',
      cta: 'Seguir agendando',
      dismissLabel: 'Ocultar esta ayuda',
      pathTitle,
    };
  }

  if (phase === 'need_catalog') {
    return {
      eyebrow: progress,
      title: serviceCatalog ? 'Cargá el catálogo' : 'Cargá productos',
      body:
        milestone === 'appointment'
          ? 'Cargá tus servicios para poder agendar. Si ya tenés lista en otro sistema, importá por CSV.'
          : 'Cargá lo que vendés para poder cobrar. Si ya tenés lista en otro sistema, importá por CSV.',
      cta: 'Ir a Catálogo',
      afterHint:
        milestone === 'appointment'
          ? 'Después: agendá el primer turno'
          : includesCashStep
            ? 'Después: abrí la caja'
            : 'Después: cobrá una venta',
      pathTitle,
    };
  }

  if (phase === 'need_cash') {
    return {
      eyebrow: progress,
      title: 'Abrí la caja',
      body: 'El turno guarda el efectivo del día. Si solo cobrás con QR o tarjeta, andá directo al POS.',
      cta: 'Abrir caja',
      afterHint: 'Después: cobrá una venta',
      pathTitle,
    };
  }

  if (phase === 'need_first_appointment') {
    return {
      eyebrow: progress,
      title: 'Agendá el primer turno',
      body: 'Elegí servicio, cliente y horario. El cobro lo hacés cuando atiendas.',
      cta: 'Nuevo turno',
      afterHint: 'Con eso ya está tu agenda andando',
      pathTitle,
    };
  }

  return {
    eyebrow: progress,
    title: 'Cobrá la primera venta',
    body: 'Buscá o escaneá en el POS, armá el carrito y cobrá.',
    cta: 'Ir al POS',
    afterHint: 'Con eso ya estás vendiendo',
    pathTitle,
  };
}
